<div data-spartan-page class="p-6 md:p-8 space-y-5"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Page Header -->
  <app-page-header
    eyebrow="Admin"
    title="XML Template Management"
    subtitle="Manage XML templates for customs provider integrations."
  >
    <div actions class="flex items-center gap-2">
      @if (!showCreateForm && !showEditForm) {
        <button hlmBtn size="sm" class="gap-2" type="button" (click)="showCreateTemplate()">
          New Template
        </button>
      }
    </div>
  </app-page-header>

  <!-- Inline alerts -->
  @if (successMessage) {
    <div class="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-3.5 text-sm text-green-900">
      <span class="flex-1">{{ successMessage }}</span>
      <button type="button" class="shrink-0 text-green-700 hover:text-green-900" (click)="clearMessages()" aria-label="Dismiss">&#x2715;</button>
    </div>
  }
  @if (errorMessage && !errorMessage.includes('mapping')) {
    <div class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3.5 text-sm text-red-900">
      <span class="flex-1">{{ errorMessage }}</span>
      <button type="button" class="shrink-0 text-red-700 hover:text-red-900" (click)="clearMessages()" aria-label="Dismiss">&#x2715;</button>
    </div>
  }

  <!-- Loading -->
  @if (isLoading) {
    <div class="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
      <span class="inline-block w-6 h-6 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
      <span class="text-sm">Loading XML templates…</span>
    </div>
  }

  <!-- Create / Edit Form -->
  @if (showCreateForm || showEditForm) {
    <div hlmCard>
      <div class="px-5 pt-4 pb-3 border-b border-border">
        <p class="text-base font-semibold leading-tight">{{ showCreateForm ? 'Create New Template' : 'Edit Template' }}</p>
      </div>
      <div class="p-5">
        <form [formGroup]="templateForm" (ngSubmit)="saveTemplate()" class="space-y-5">

          <!-- Basic info row -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Template Name <span class="text-red-500">*</span></label>
              <input type="text"
                class="w-full h-9 rounded-md border border-border-strong bg-surface text-sm px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                [class.border-red-400]="hasFieldError('name')"
                formControlName="name"
                placeholder="Enter template name">
              @if (hasFieldError('name')) {
                <p class="text-xs text-red-600 mt-1">{{ getFieldError('name') }}</p>
              }
            </div>
            <div>
              <label class="block text-xs font-medium text-muted-foreground mb-1.5">Provider <span class="text-red-500">*</span></label>
              <input type="text"
                class="w-full h-9 rounded-md border border-border-strong bg-surface text-sm px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                [class.border-red-400]="hasFieldError('provider')"
                formControlName="provider"
                placeholder="Enter provider name">
              @if (hasFieldError('provider')) {
                <p class="text-xs text-red-600 mt-1">{{ getFieldError('provider') }}</p>
              }
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-muted-foreground mb-1.5">Description <span class="text-red-500">*</span></label>
            <textarea
              class="w-full rounded-md border border-border-strong bg-surface text-sm px-3 py-2 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition resize-none"
              [class.border-red-400]="hasFieldError('description')"
              formControlName="description"
              rows="2"
              placeholder="Enter template description"></textarea>
            @if (hasFieldError('description')) {
              <p class="text-xs text-red-600 mt-1">{{ getFieldError('description') }}</p>
            }
          </div>

          <!-- XML Content — monospace font per design spec -->
          <div>
            <label class="block text-xs font-medium text-muted-foreground mb-1.5">XML Template Content <span class="text-red-500">*</span></label>
            <textarea
              class="w-full rounded-md border border-border-strong bg-surface text-sm px-3 py-2 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition resize-y font-mono text-[12.5px] leading-relaxed"
              [class.border-red-400]="hasFieldError('xmlContent')"
              formControlName="xmlContent"
              rows="15"
              placeholder="Enter XML template with placeholders like &#123;fieldName&#125;"></textarea>
            @if (hasFieldError('xmlContent')) {
              <p class="text-xs text-red-600 mt-1">{{ getFieldError('xmlContent') }}</p>
            }
            <p class="text-[11px] text-muted-foreground mt-1">Use placeholders like <code class="font-mono bg-stone-100 px-1 rounded">{{ '{' }}{{ '{' }}fieldName{{ '}' }}{{ '}' }}</code> for dynamic fields</p>
          </div>

          <!-- Field Mappings -->
          <div class="space-y-3">
            <p class="text-sm font-medium text-muted-foreground">Field Mappings</p>

            <!-- Add new mapping -->
            <div hlmCard class="p-4">
              <div class="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div>
                  <label class="block text-xs font-medium text-muted-foreground mb-1.5">XML Field (Placeholder) <span class="text-red-500">*</span></label>
                  <input type="text"
                    class="w-full h-8 rounded-md border border-border-strong bg-surface text-xs px-3 placeholder:text-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                    name="xmlFieldInput"
                    [value]="newFieldMapping.xmlField"
                    (input)="updateXmlField($event)"
                    placeholder="e.g., registryNumber">
                </div>
                <div>
                  <label class="block text-xs font-medium text-muted-foreground mb-1.5">Form Field <span class="text-red-500">*</span></label>
                  <select
                    class="w-full h-8 rounded-md border border-border-strong bg-surface text-xs px-2 appearance-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent transition"
                    name="formFieldSelect"
                    [value]="newFieldMapping.formField"
                    (change)="updateFormField($event)">
                    <option value="">Select field…</option>
                    @for (field of availableFormFields; track field) {
                      <option [value]="field">{{ field }}</option>
                    }
                    <option value="static:">Static Value</option>
                  </select>
                </div>
                <div class="flex items-center gap-2 h-8">
                  <label class="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                    <input type="checkbox" class="w-3.5 h-3.5 rounded accent-primary"
                      name="isRequiredCheckbox"
                      [checked]="newFieldMapping.isRequired"
                      (change)="updateIsRequired($event)">
                    Required
                  </label>
                </div>
                <div>
                  <button type="button" hlmBtn size="sm" class="w-full h-8" [disabled]="isAddButtonDisabled()" (click)="addFieldMapping()">
                    Add Mapping
                  </button>
                </div>
              </div>
              @if (errorMessage && errorMessage.includes('mapping')) {
                <div class="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900 mt-3">
                  {{ errorMessage }}
                </div>
              }
            </div>

            <!-- Existing mappings table -->
            @if (fieldMappings.length > 0) {
              <div class="overflow-x-auto">
                <table class="w-full text-xs" aria-label="Field mappings">
                  <thead>
                    <tr class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                      <th class="px-3 py-2 text-left font-medium">XML Field</th>
                      <th class="px-3 py-2 text-left font-medium">Form Field</th>
                      <th class="px-3 py-2 text-left font-medium">Required</th>
                      <th class="px-3 py-2 text-left font-medium w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (mapping of fieldMappings; track $index) {
                      <tr class="border-b border-border last:border-0 even:bg-stone-50/60 hover:bg-stone-100/70 transition-colors">
                        <td class="px-3 h-9 font-mono text-[11px]">{{ mapping.xmlField }}</td>
                        <td class="px-3 h-9">
                          @if (!mapping.formField.startsWith('static:')) {
                            <span class="inline-flex items-center rounded-md px-2 h-5 text-[10px] font-medium bg-accent-light text-accent ring-1 ring-blue-200/70">{{ mapping.formField }}</span>
                          } @else {
                            <span class="inline-flex items-center rounded-md px-2 h-5 text-[10px] font-medium bg-stone-100 text-stone-700 ring-1 ring-stone-200">Static: {{ mapping.formField.replace('static:', '') || '(empty)' }}</span>
                          }
                        </td>
                        <td class="px-3 h-9">
                          @if (mapping.isRequired) {
                            <span class="text-green-700">&#x2713;</span>
                          } @else {
                            <span class="text-stone-400">&mdash;</span>
                          }
                        </td>
                        <td class="px-3 h-9">
                          <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0 text-red-500 hover:text-red-600" (click)="removeFieldMapping($index)" aria-label="Remove mapping">&#x1F5D1;</button>
                        </td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
            @if (fieldMappings.length === 0) {
              <p class="text-xs text-muted-foreground text-center py-4">No field mappings added yet. Add at least one mapping above.</p>
            }
          </div>

          <!-- Form Actions -->
          <div class="flex gap-2 pt-2 border-t border-border">
            <button type="submit" hlmBtn size="sm" class="gap-2" [disabled]="isSaving || fieldMappings.length === 0">
              @if (isSaving) {
                <span class="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
              }
              {{ isSaving ? 'Saving…' : (showEditForm ? 'Update Template' : 'Create Template') }}
            </button>
            <button type="button" hlmBtn variant="outline" size="sm" (click)="cancelForm()">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  }

  <!-- Templates List -->
  @if (!showCreateForm && !showEditForm && !isLoading) {
    <div hlmCard>
      <div class="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
        <div class="flex items-center gap-2">
          <p class="text-base font-semibold leading-tight">Available Templates</p>
          <span class="inline-flex items-center rounded-md px-2 h-5 text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">{{ xmlTemplates.length }}</span>
        </div>
        <div class="flex items-center gap-2">
          <button type="button" hlmBtn variant="outline" size="sm" class="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50" (click)="clearAllSavedTemplates()" title="Reset to defaults">
            Reset Templates
          </button>
        </div>
      </div>
      <div class="p-5">
        @if (xmlTemplates.length > 0) {
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            @for (template of xmlTemplates; track template.id) {
              <div hlmCard class="flex flex-col">
                <div class="px-4 pt-3.5 pb-3 border-b border-border flex items-start justify-between gap-2">
                  <p class="text-sm font-semibold leading-tight truncate">{{ template.name }}</p>
                  <span class="inline-flex items-center rounded-md px-2 h-5 text-[10px] font-medium bg-accent-light text-accent ring-1 ring-blue-200/70 shrink-0">{{ template.provider }}</span>
                </div>
                <div class="p-4 flex-1">
                  <p class="text-xs text-muted-foreground mb-3 line-clamp-2">{{ template.description }}</p>
                  <p class="text-[10.5px] text-muted-foreground">Updated: {{ template.updatedAt | date:'medium' }}</p>
                </div>
                <div class="px-4 pb-3 flex gap-1.5">
                  <button type="button" hlmBtn variant="outline" size="sm" class="h-7 text-xs flex-1" (click)="previewTemplate(template)">Preview</button>
                  <button type="button" hlmBtn variant="outline" size="sm" class="h-7 text-xs flex-1" (click)="editTemplate(template)" [disabled]="template.id === 'ekonek-air-manifest'">Edit</button>
                  <button type="button" hlmBtn variant="ghost" size="sm" class="h-7 w-7 p-0 text-red-500 hover:text-red-600" (click)="deleteTemplate(template)" [disabled]="template.id === 'ekonek-air-manifest'" aria-label="Delete template">&#x1F5D1;</button>
                </div>
              </div>
            }
          </div>
        }
        @if (xmlTemplates.length === 0) {
          <div class="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
            <p class="text-sm">No templates available</p>
          </div>
        }
      </div>
    </div>
  }
</div>

<!-- Template Preview Modal -->
@if (showPreview) {
  <div class="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" aria-label="Template Preview"
       (click)="closePreview()" (keydown.escape)="closePreview()">
    <div class="absolute inset-0 bg-stone-900/40 backdrop-blur-[2px]"></div>
    <div class="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-surface rounded-xl border border-border shadow-xl" (click)="$event.stopPropagation()">
      <div class="px-6 pt-5 pb-4 border-b border-border flex items-center justify-between sticky top-0 bg-surface z-10">
        <h2 class="text-base font-semibold">Preview: {{ selectedTemplate?.name }}</h2>
        <button type="button" hlmBtn variant="ghost" size="sm" class="h-8 w-8 p-0" (click)="closePreview()" aria-label="Close">&#x2715;</button>
      </div>
      <div class="p-6 space-y-5">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div hlmCard>
            <div class="px-5 pt-4 pb-3 border-b border-border">
              <p class="text-sm font-semibold">Template Information</p>
            </div>
            <div class="p-5 text-sm space-y-3">
              <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Name</p><p class="font-medium">{{ selectedTemplate?.name }}</p></div>
              <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Provider</p><p>{{ selectedTemplate?.provider }}</p></div>
              <div><p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Description</p><p class="text-muted-foreground">{{ selectedTemplate?.description }}</p></div>
              <div>
                <p class="text-[11px] text-muted-foreground uppercase tracking-wider mb-0.5">Field Mappings</p>
                <span class="inline-flex items-center rounded-md px-2 h-5 text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                  {{ (selectedTemplate?.fieldMappings | keyvalue)?.length || 0 }} fields
                </span>
              </div>
            </div>
          </div>
          <div hlmCard>
            <div class="px-5 pt-4 pb-3 border-b border-border">
              <p class="text-sm font-semibold">Field Mappings</p>
            </div>
            <div class="p-5 overflow-y-auto max-h-56">
              @for (mapping of selectedTemplate?.fieldMappings | keyvalue; track mapping.key; let odd = $odd) {
                <div class="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <code class="font-mono text-[11px] text-muted-foreground shrink-0">{{ mapping.key }}</code>
                  <span class="text-muted-foreground text-xs">&rarr;</span>
                  <span class="inline-flex items-center rounded-md px-2 h-5 text-[10px] font-medium bg-accent-light text-accent ring-1 ring-blue-200/70">{{ mapping.value }}</span>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- XML Content — monospace per spec -->
        <div hlmCard>
          <div class="px-5 pt-4 pb-3 border-b border-border">
            <p class="text-sm font-semibold">XML Template Content</p>
          </div>
          <div class="p-0 overflow-hidden rounded-b-lg">
            <pre class="p-5 overflow-x-auto text-[12.5px] font-mono leading-relaxed bg-stone-950 text-stone-100 rounded-b-lg m-0"><code [innerHTML]="getHighlightedXml()"></code></pre>
          </div>
        </div>
      </div>
      <div class="px-6 py-4 border-t border-border flex justify-end bg-stone-50/50 rounded-b-xl">
        <button type="button" hlmBtn variant="outline" size="sm" (click)="closePreview()">Close</button>
      </div>
    </div>
  </div>
}
